import { useState } from 'react';
import { api } from '../api';
import { SyncBadge } from '../components/Sync';
import { Metric, Panel, toast } from '../components/ui';
import { requestSync, useEngine } from '../engine/client';
import { agoLabel, errorMessage } from '../format';
import { useApi } from '../hooks';
import { cloudCall, logoutUrl } from './api';
import { useCloud } from './state';

const PROVIDER: Record<string, string> = { google: '구글', kakao: '카카오', test: '시험' };

/** 클라우드 사이트로 연 기기: 계정 · 동기화 · PC 연결 코드 · (관리자) 초대 */
export function DeviceCloudPanel() {
  const me = useCloud();
  const { sync } = useEngine();
  const [pcToken, setPcToken] = useState<string | null>(null);
  const [invite, setInvite] = useState<string | null>(null);
  if (!me?.user) return null;
  const user = me.user;
  const makeToken = async () => {
    try {
      setPcToken((await cloudCall<{ token: string }>('POST', 'api/cloud/tokens', { label: 'PC' })).token);
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  const makeInvite = async () => {
    try {
      const { code } = await cloudCall<{ code: string }>('POST', 'api/cloud/invites', {});
      setInvite(`${location.origin}/?invite=${code}`);
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  return (
    <Panel title="계정 · 클라우드" className="span-2">
      <div className="row-gap">
        <SyncBadge />
        <button type="button" className="btn btn-sm" onClick={requestSync}>지금 동기화</button>
      </div>
      <div className="metrics-2">
        <Metric label="로그인" value={user.name} sub={`${PROVIDER[user.provider] ?? user.provider} 계정${user.role === 'admin' ? ' · 관리자' : ''}`} />
        <Metric label="마지막 동기화" value={sync.lastSync ? agoLabel(sync.lastSync) : '—'} sub={sync.error ?? `보낼 기록 ${sync.pending}개`} />
      </div>
      <p className="small muted">기록은 이 기기에서 잠근 뒤 클라우드에 올라갑니다. 다른 기기에서 열 때는 처음 적어 둔 복구 코드가 필요합니다.</p>

      <h3 className="sub-title">PC 와 잇기</h3>
      <p className="small muted">PC 의 APEX(설정 → 클라우드 연결)에 아래 주소·코드와 복구 코드를 넣으면, PC 가 켜져 있을 때 PC 에도 기록이 모이고 백업됩니다.</p>
      {pcToken ? (
        <div className="cloud-secret">
          <div><span className="muted small">사이트 주소</span> <code>{location.origin}</code></div>
          <div><span className="muted small">PC 연결 코드</span> <code>{pcToken}</code></div>
          <button type="button" className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(pcToken)}>코드 복사</button>
          <p className="small muted">이 코드는 지금 한 번만 보입니다.</p>
        </div>
      ) : (
        <button type="button" className="btn btn-sm" onClick={() => void makeToken()}>PC 연결 코드 만들기</button>
      )}

      {user.role === 'admin' && (
        <>
          <h3 className="sub-title">다른 사람 초대</h3>
          <p className="small muted">초대 링크로 들어온 사람만 가입할 수 있습니다. 링크 하나에 한 사람. 각자의 기록은 자기 복구 코드로만 열려서 서로 볼 수 없습니다.</p>
          {invite ? (
            <div className="cloud-secret">
              <code>{invite}</code>
              <button type="button" className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(invite)}>링크 복사</button>
            </div>
          ) : (
            <button type="button" className="btn btn-sm" onClick={() => void makeInvite()}>초대 링크 만들기</button>
          )}
        </>
      )}
      <div className="form-actions">
        <a className="btn btn-ghost btn-sm" href={logoutUrl()}>로그아웃</a>
      </div>
    </Panel>
  );
}

/** PC 서버 화면: 클라우드 우체통에 PC 잇기 */
export function PcCloudPanel() {
  const st = useApi(api.cloudStatus);
  const [form, setForm] = useState({ url: '', token: '', code: '' });
  const [busy, setBusy] = useState(false);
  const run = async (what: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await what();
      toast(done);
      st.reload();
    } catch (e) {
      toast(errorMessage(e), 'err');
      st.reload();
    } finally {
      setBusy(false);
    }
  };
  const s = st.data;
  return (
    <Panel title="클라우드 연결" className="span-2">
      {s?.linked ? (
        <>
          <div className="metrics-2">
            <Metric label="연결된 사이트" value={s.user?.name ?? '연결됨'} sub={s.url} />
            <Metric label="마지막으로 맞춘 때" value={s.last_ok ? agoLabel(s.last_ok) : '—'} sub={s.error ?? `보낼 기록 ${s.pending}개 · 켜져 있는 동안 1분마다`} />
          </div>
          <div className="row-gap">
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void run(api.cloudSync, '클라우드와 맞췄습니다')}>지금 맞추기</button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => window.confirm('클라우드 연결을 끊을까요? PC 의 기록은 그대로 남습니다.') && void run(api.cloudUnlink, '연결을 끊었습니다')}>연결 끊기</button>
          </div>
        </>
      ) : (
        <form className="cloud-form" onSubmit={(e) => { e.preventDefault(); void run(() => api.cloudLink(form), '클라우드에 이었습니다 — PC 기록을 올렸습니다'); }}>
          <p className="small muted">폰(클라우드 사이트) 설정 → '계정 · 클라우드' → 'PC 연결 코드 만들기'에서 받은 주소·코드와, 처음 적어 둔 복구 코드를 넣으세요. 이으면 PC 의 기록 전부를 잠가서 올리고, 그 뒤로는 켜져 있는 동안 1분마다 맞춥니다.</p>
          <input className="input" aria-label="사이트 주소" placeholder="https://apex-○○.pages.dev" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <input className="input" aria-label="PC 연결 코드" placeholder="apx_…" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} />
          <input className="input" aria-label="복구 코드" placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <button type="submit" className="btn btn-primary" disabled={busy || !form.url || !form.token || !form.code}>{busy ? '잇는 중…' : '잇기'}</button>
          {s?.error && <p className="small gate-error">{s.error}</p>}
        </form>
      )}
    </Panel>
  );
}
